import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

// 商品圖畫廊：
// - 電腦版：滑鼠移到主圖 → 圖上出現放大鏡「框框」，右側彈出放大細節面板（對齊 lunaluna 效果）。
// - 點主圖 → 開全螢幕大圖檢視（左右切換、縮圖跳頁、鍵盤 ←/→/Esc）。
// - 手機版：不做 hover 放大，改為點圖進大圖（可手指縮放）。

const MAGNIFY = 2.5; // 放大倍率（框框大小 = 主圖 / 倍率；面板呈現放大後細節）

const navBtnStyle = (side, big = false) => ({
  position: 'absolute', top: '50%', [side]: big ? 20 : 10, transform: 'translateY(-50%)',
  width: big ? 52 : 36, height: big ? 52 : 36, borderRadius: '50%', border: 'none',
  background: big ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.9)',
  color: big ? '#fff' : '#222957', fontSize: big ? 22 : 16, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3,
  boxShadow: big ? 'none' : '0 2px 8px rgba(34,41,87,0.18)',
});

export default function ProductGallery({ images = [], alt = '', isMobile = false, brand = '#222957', border = '#DCDEF0' }) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const len = list.length;
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const safe = len ? ((idx % len) + len) % len : 0;
  const cur = len ? list[safe] : null;

  const go = useCallback((d) => setIdx((i) => (len ? (((i + d) % len) + len) % len : 0)), [len]);

  // 大圖檢視時的鍵盤操作
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go]);

  // 放大鏡（框框＋右側面板）
  const mainRef = useRef(null);
  const [lens, setLens] = useState(null); // { w,h,lw,lh,lx,ly,bgPos,bgSize } | null

  const onMove = (e) => {
    if (isMobile || !cur || !mainRef.current) return;
    const r = mainRef.current.getBoundingClientRect();
    const w = r.width;
    const h = r.height;
    const lw = w / MAGNIFY;
    const lh = h / MAGNIFY;
    let lx = e.clientX - r.left - lw / 2;
    let ly = e.clientY - r.top - lh / 2;
    lx = Math.min(Math.max(lx, 0), w - lw);
    ly = Math.min(Math.max(ly, 0), h - lh);
    // 右側細節面板改用 fixed 定位（配合 portal 疊到最上層）：預設放右邊，
    // 右邊空間不夠就改放左邊，避免超出視窗或被右欄內容擋住。
    const gap = 16;
    let panelLeft = r.right + gap;
    if (panelLeft + w > window.innerWidth - 8) panelLeft = r.left - gap - w;
    setLens({
      w, h, lw, lh, lx, ly,
      panelLeft, panelTop: r.top,
      bgPos: `${-lx * MAGNIFY}px ${-ly * MAGNIFY}px`,
      bgSize: `${w * MAGNIFY}px ${h * MAGNIFY}px`,
    });
  };
  const onLeave = () => setLens(null);
  // 換圖時清掉放大鏡狀態
  useEffect(() => { setLens(null); }, [safe]);

  return (
    <div>
      {/* 主圖 */}
      <div style={{ position: 'relative' }}>
        <div
          ref={mainRef}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          onClick={() => cur && setOpen(true)}
          style={{
            position: 'relative', width: '100%', aspectRatio: '1 / 1', overflow: 'hidden',
            borderRadius: 16, border: `1px solid ${border}`, background: '#F2F3F9',
            boxShadow: '0 12px 32px rgba(34,41,87,0.12)', cursor: cur ? 'crosshair' : 'default',
          }}
        >
          {cur && (
            <img
              src={cur}
              alt={alt}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          {/* 放大鏡框框 */}
          {lens && (
            <div
              style={{
                position: 'absolute', left: lens.lx, top: lens.ly, width: lens.lw, height: lens.lh,
                border: '2px solid rgba(255,255,255,0.9)', boxShadow: '0 0 0 1px rgba(34,41,87,0.35), 0 2px 8px rgba(0,0,0,0.25)',
                background: 'rgba(34,41,87,0.12)', pointerEvents: 'none', zIndex: 2,
              }}
            />
          )}
        </div>

        {/* 右側放大細節面板（彈窗；用 portal + fixed 疊到最上層，避免被右欄手圍等內容擋住） */}
        {lens && cur && createPortal(
          <div
            style={{
              position: 'fixed', top: lens.panelTop, left: lens.panelLeft, width: lens.w, height: lens.h,
              borderRadius: 12, border: `1px solid ${border}`, background: '#fff',
              boxShadow: '0 16px 40px rgba(34,41,87,0.22)', overflow: 'hidden',
              backgroundImage: `url("${cur}")`, backgroundRepeat: 'no-repeat',
              backgroundSize: lens.bgSize, backgroundPosition: lens.bgPos,
              pointerEvents: 'none', zIndex: 2000,
            }}
          />,
          document.body
        )}

        {len > 1 && (
          <>
            <button type="button" aria-label="上一張" style={navBtnStyle('left')}
              onClick={(e) => { e.stopPropagation(); go(-1); }}>
              <LeftOutlined />
            </button>
            <button type="button" aria-label="下一張" style={navBtnStyle('right')}
              onClick={(e) => { e.stopPropagation(); go(1); }}>
              <RightOutlined />
            </button>
            <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, zIndex: 2 }}>
              {safe + 1} / {len}
            </div>
          </>
        )}
        {/* 操作提示（電腦版、未 hover 時） */}
        {!isMobile && cur && !lens && (
          <div style={{ position: 'absolute', left: 12, bottom: 10, background: 'rgba(0,0,0,0.45)', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, zIndex: 2, pointerEvents: 'none' }}>
            移動滑鼠可放大 · 點擊看大圖
          </div>
        )}
      </div>

      {/* 縮圖列 */}
      {len > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {list.map((url, i) => (
            <button
              key={url + i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`檢視第 ${i + 1} 張`}
              style={{
                width: 56, height: 56, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                border: `2px solid ${i === safe ? brand : '#DCDEF0'}`,
                background: '#fff', flex: '0 0 auto',
              }}
            >
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}

      {/* 全螢幕大圖檢視 */}
      {open && cur && createPortal(
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
          }}
        >
          <button
            onClick={() => setOpen(false)}
            aria-label="關閉"
            style={{
              position: 'fixed', top: 16, right: 16, zIndex: 4, width: 40, height: 40,
              borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer',
            }}
          >×</button>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={(e) => e.stopPropagation()}>
            <img
              src={cur}
              alt={alt}
              style={{
                width: 'min(90vw, 85vh, 900px)', height: 'min(90vw, 85vh, 900px)',
                objectFit: 'contain', touchAction: 'pinch-zoom', display: 'block',
              }}
            />
            {len > 1 && (
              <>
                <button type="button" aria-label="上一張" style={navBtnStyle('left', true)}
                  onClick={(e) => { e.stopPropagation(); go(-1); }}>
                  <LeftOutlined />
                </button>
                <button type="button" aria-label="下一張" style={navBtnStyle('right', true)}
                  onClick={(e) => { e.stopPropagation(); go(1); }}>
                  <RightOutlined />
                </button>
              </>
            )}
          </div>

          {len > 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', maxWidth: '92vw' }}
              onClick={(e) => e.stopPropagation()}>
              {list.map((url, i) => (
                <button
                  key={url + i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`檢視第 ${i + 1} 張`}
                  style={{
                    width: 48, height: 48, padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                    border: `2px solid ${i === safe ? '#fff' : 'rgba(255,255,255,0.35)'}`,
                    opacity: i === safe ? 1 : 0.6, background: '#fff', flex: '0 0 auto',
                  }}
                >
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}

          {!isMobile && (
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>←/→ 切換 · Esc 關閉</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
